import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, RefreshCw } from 'lucide-react';

const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 60, label: 'Last 60 days' },
  { value: 90, label: 'Last 90 days' }
];

// Metrics we always show at the top as KPI cards (if present in totals)
const HEADLINE_METRIC_ORDER = [
  'spend',
  'impressions',
  'clicks',
  'ctr',
  'cpc',
  'cpm',
  'conversion',
  'cost_per_conversion',
  'conversion_rate',
  'onsite_shopping',
  'onsite_shopping_roas',
  'complete_payment',
  'complete_payment_roas'
];

function formatNumber(val, key) {
  if (val === null || val === undefined || val === '') return '—';
  const n = Number(val);
  if (!Number.isFinite(n)) return String(val);
  const isMoney = /spend|cost_per|cpc|cpm|purchase_value|shopping_value/.test(key);
  const isRate = /rate|ctr|roas|frequency/.test(key);
  if (isMoney) return n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
  if (isRate) return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function labelize(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function TiktokAdsTest() {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const brandId = localStorage.getItem('currentBrandId') || '';

  const fetchDashboard = async (range = days) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`https://api.glowytics.com/api/tiktok-ads/dashboard?days=${range}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to fetch TikTok dashboard');
      setData(json);
    } catch (e) {
      console.error('TikTok dashboard error:', e);
      setError(e.message);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (brandId) fetchDashboard(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const headlineMetrics = useMemo(() => {
    const totals = data?.reporting?.totals || {};
    const result = [];
    for (const k of HEADLINE_METRIC_ORDER) {
      if (k in totals) result.push({ key: k, value: totals[k] });
    }
    return result;
  }, [data]);

  const rows = data?.reporting?.rows || [];
  const allMetricKeys = useMemo(() => {
    const set = new Set();
    for (const r of rows) {
      for (const k of Object.keys(r?.metrics || {})) set.add(k);
    }
    return Array.from(set);
  }, [rows]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">TikTok Ads — Test Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Pulls advertiser info, campaigns, and all campaign-level metrics directly from the TikTok
            Business API for the active connection.
          </p>
          {data?.connection && (
            <p className="text-xs text-gray-500 mt-2">
              Account: <span className="font-medium text-gray-700">{data.connection.accountName || '—'}</span>
              {' '}· advertiser_id: <code className="bg-gray-100 px-1 rounded">{data.connection.advertiserId}</code>
              {' '}· {data.dateRange?.startDate} → {data.dateRange?.endDate} ({data.dateRange?.days}d)
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => {
              const v = Number(e.target.value);
              setDays(v);
              fetchDashboard(v);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={() => fetchDashboard(days)}
            disabled={loading || !brandId}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </div>

      {!brandId && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          No brand selected. Switch to a brand that has an active TikTok Ads connection.
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="p-8 text-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading TikTok data…
        </div>
      )}

      {data && (
        <>
          {/* API-level status strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatusCard
              title="Advertiser Info"
              ok={data.advertiser?.ok}
              error={data.advertiser?.error}
              detail={
                data.advertiser?.info
                  ? `${data.advertiser.info.name || '—'} · ${data.advertiser.info.currency || ''} · ${data.advertiser.info.country || ''}`
                  : null
              }
            />
            <StatusCard
              title="Campaigns"
              ok={data.campaigns?.ok}
              error={data.campaigns?.error}
              detail={`${data.campaigns?.count || 0} campaign(s)`}
            />
            <StatusCard
              title="Reporting"
              ok={data.reporting?.ok}
              error={data.reporting?.error}
              detail={`${data.reporting?.count || 0} campaign row(s) · ${data.metricsRequested?.length || 0} metrics requested`}
            />
          </div>

          {/* Headline metric cards */}
          {headlineMetrics.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-700 mb-2">Totals ({data.dateRange?.days}d)</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {headlineMetrics.map((m) => (
                  <div key={m.key} className="p-3 bg-white border border-gray-200 rounded-lg">
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">{labelize(m.key)}</div>
                    <div className="text-lg font-semibold text-gray-900 mt-1">{formatNumber(m.value, m.key)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Campaign list */}
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Campaigns</h2>
            {!data.campaigns?.ok && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 mb-2">
                TikTok API error: {data.campaigns?.error?.message} (code {data.campaigns?.error?.code})
              </div>
            )}
            {data.campaigns?.list?.length ? (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">Campaign</th>
                      <th className="px-3 py-2 text-left">ID</th>
                      <th className="px-3 py-2 text-left">Objective</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Budget</th>
                      <th className="px-3 py-2 text-left">Budget Mode</th>
                      <th className="px-3 py-2 text-left">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.campaigns.list.map((c) => (
                      <tr key={c.campaign_id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-900">{c.campaign_name || '—'}</td>
                        <td className="px-3 py-2 text-gray-500 font-mono text-xs">{c.campaign_id}</td>
                        <td className="px-3 py-2 text-gray-600">{c.objective_type || c.objective || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            /ENABLE|DELIVER|ACTIVE/.test(String(c.operation_status || c.status || ''))
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {c.operation_status || c.status || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{c.budget ?? '—'}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{c.budget_mode || '—'}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{c.create_time || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No campaigns returned.</p>
            )}
          </div>

          {/* Per-campaign metrics table */}
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-2">
              Campaign Metrics ({data.dateRange?.days}d)
            </h2>
            {!data.reporting?.ok && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 mb-2">
                TikTok API error: {data.reporting?.error?.message} (code {data.reporting?.error?.code})
              </div>
            )}
            {rows.length ? (
              <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[600px]">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-600 uppercase sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left sticky left-0 bg-gray-50">Campaign</th>
                      {allMetricKeys.map((k) => (
                        <th key={k} className="px-3 py-2 text-right whitespace-nowrap">{labelize(k)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r) => (
                      <tr key={r.campaign_id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 whitespace-nowrap">
                          <div className="font-medium">{r.campaign_name || '—'}</div>
                          <div className="text-[10px] text-gray-400 font-mono">{r.campaign_id}</div>
                        </td>
                        {allMetricKeys.map((k) => (
                          <td key={k} className="px-3 py-2 text-right text-gray-700 tabular-nums whitespace-nowrap">
                            {formatNumber(r.metrics?.[k], k)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No reporting rows. (Likely no spend in the selected range.)</p>
            )}
          </div>

          {/* Raw JSON debug */}
          <details className="mt-6">
            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
              Raw API response (debug)
            </summary>
            <pre className="mt-2 p-3 bg-gray-900 text-gray-100 text-[11px] leading-relaxed rounded-lg overflow-auto max-h-[500px]">
              {JSON.stringify(data, null, 2)}
            </pre>
          </details>
        </>
      )}
    </div>
  );
}

function StatusCard({ title, ok, error, detail }) {
  return (
    <div className={`p-3 rounded-lg border ${ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-800">{title}</div>
        <span className={`text-xs px-2 py-0.5 rounded ${ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {ok ? 'OK' : 'ERROR'}
        </span>
      </div>
      {detail && <div className="text-xs text-gray-600 mt-1">{detail}</div>}
      {!ok && error && (
        <div className="text-xs text-red-700 mt-1">
          {error.message} {error.code != null ? `(code ${error.code})` : ''}
        </div>
      )}
    </div>
  );
}
